import { GeneralIndicatorInfo } from 'custom_hooks/data_hooks/indicator/shared';
import { useWorkbenchValue } from 'pages/workbench/state';
import { IndicatorInfo } from 'pages/workbench/types';
import { useMemo } from 'react';
import { toMap } from 'utils/array';
import { queryClient } from 'utils/query';

export type TrendIndicator = Readonly<{
    indicatorId: string;
    indicator: GeneralIndicatorInfo;
    color: string;
}>;

export type UseTrendIndicatorsResult = {
    data: TrendIndicator[] | undefined;
    isFetching: boolean;
};

// Invalidates the legend panel's indicator queries, which feed resolvedIndicators.
export function invalidateTrendIndicators() {
    queryClient.invalidateQueries({
        queryKey: ['indicator-multi-select'],
    });
    queryClient.invalidateQueries({
        queryKey: ['data-platform-indicator-multi-select'],
    });
}

function resolveFromStore(
    resolvedIndicators: GeneralIndicatorInfo[] | undefined,
    indicatorIds: string[],
    colorLookup: Map<string, string>,
): TrendIndicator[] | undefined {
    if (!resolvedIndicators || indicatorIds.length === 0) {
        return undefined;
    }

    const indicatorMap = toMap(resolvedIndicators, (i) => i.Id);
    const result: TrendIndicator[] = [];

    for (const id of indicatorIds) {
        const indicator = indicatorMap.get(id);
        const color = colorLookup.get(id);
        if (!indicator || !color) {
            return undefined;
        }
        result.push({ indicatorId: id, indicator, color });
    }

    return result;
}

export function useTrendIndicators({
    indicatorInfo,
}: Readonly<{
    indicatorInfo: IndicatorInfo[];
}>): UseTrendIndicatorsResult {
    const resolvedIndicators = useWorkbenchValue((state) => state.resolvedIndicators);

    const colorLookup = useMemo(
        () =>
            toMap(
                indicatorInfo,
                (info) => info.indicatorId,
                (info) => info.color,
            ),
        [indicatorInfo],
    );

    const indicatorIds = useMemo(
        () => indicatorInfo.map((info) => info.indicatorId),
        [indicatorInfo],
    );

    const data = useMemo(
        () => resolveFromStore(resolvedIndicators, indicatorIds, colorLookup),
        [resolvedIndicators, indicatorIds, colorLookup],
    );

    return { data, isFetching: !data && indicatorIds.length > 0 };
}
